import {isUserLoggedIn} from '@Libraries/user.library';
import {MULTI_MEDIA_SOURCE_GETTY} from '@Libraries/user-media-library';
import {EMAIL_MAKER_COMMANDS, EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import type {Component, ComponentModelDefinition, ComponentView, ComponentViewDefinition} from 'grapesjs';
import {EmailMakerMediaImageModal, EmailMakerMediaType} from '@EmailMakerReact/email-maker-react.types';
import type {ComponentRegistrationContext} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';
import {asViewEvents, RESTORED_GRAPESJS_IMAGE_VIEW_EVENTS, matchByTagAndAttr, mediaViewEvents} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';
import {getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';

export function registerMediaTypes({
  editor,
  defaultToolbarOptions,
  unstylableEmailStyles,
  dispatchDblClickOnDoubleTap,
  mediaHoverEventHandlers,
  mediaClickEventHandler,
  openChangeImageModal,
  mediaResizeOpts,
  initRemovalOfUnsupportedCssProperties,
}: ComponentRegistrationContext): void {
  const cmpt = editor.Components;
  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.IMAGE,
    isComponent: matchByTagAndAttr('IMG', 'data-is-pmw-design', EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN),
    model: {
      defaults: {
        trackingName: 'Design',
        name: window.i18next.t('pmwjs_my_design'),
        badgable: true,
        removable: true,
        highlightable: false,
        toolbar: defaultToolbarOptions,
        resizable: mediaResizeOpts,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        style: {
          width: '50%',
          height: 'auto',
          display: 'block',
          margin: 'auto',
          'max-width': '100%',
          padding: 0,
          opacity: '100%',
          'border-color': '#FFFFFF',
          'border-width': '0px',
          'border-radius': '0',
        },
        attributes: {
          'data-is-pmw-design': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN,
          class: 'pmw-media-type',
          alt: window.i18next.t('pmwjs_my_design'),
          'aria-hidden': 'true',
        },
        src: window.PMW.util.asset_url('images/emailmaker/my_design_placeholder.png'),
        [EMAIL_MAKER_PROPS.DESIGN_ID]: undefined,
        [EMAIL_MAKER_PROPS.DESIGN_PAGE_ID]: undefined,
        [EMAIL_MAKER_PROPS.DESIGN_MODIFIED]: true,
        [EMAIL_MAKER_PROPS.IS_INTERNAL]: false,
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        editor.runCommand(EMAIL_MAKER_COMMANDS.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, {component: this});
      },
    },
    view: {
      onActive(this: ComponentViewDefinition & ComponentView, ev: Event | undefined) {
        if (ev) {
          ev.stopPropagation();
          ev.preventDefault();
        }

        const id = this.model ? getStringProp(this.model, EMAIL_MAKER_PROPS.DESIGN_ID) : undefined;
        const creatorID = this.model ? getStringProp(this.model, EMAIL_MAKER_PROPS.DESIGN_CREATOR_ID) : undefined;

        if (!id) {
          editor.runCommand(EMAIL_MAKER_COMMANDS.OPEN_DESIGN_SELECTOR, {model: this.model, isMultiSelectAllowed: false, replaceComponent: true});
          return;
        }

        if (!isUserLoggedIn()) {
          editor.runCommand(EMAIL_MAKER_COMMANDS.AUTHENTICATE_USER, {
            cb: (): void => {
              editor.runCommand(EMAIL_MAKER_COMMANDS.OPEN_POSTERMAKER, {id, creatorID});
            },
          });
        } else {
          editor.runCommand(EMAIL_MAKER_COMMANDS.OPEN_POSTERMAKER, {id, creatorID});
        }
      },
      events: asViewEvents({
        dblclick: 'onActive',
        touchend: dispatchDblClickOnDoubleTap,
        click: mediaClickEventHandler,
        ...RESTORED_GRAPESJS_IMAGE_VIEW_EVENTS,
      }),
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.IMAGE,
    model: {
      defaults: {
        trackingName: 'Image',
        toolbar: defaultToolbarOptions,
        resizable: mediaResizeOpts,
        removable: true,
        highlightable: false,
        name: window.i18next.t('pmwjs_image'),
        badgable: true,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        attributes: {
          [EMAIL_MAKER_PROPS.IS_PMW_IMAGE]: '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE,
          class: 'pmw-media-type',
          alt: window.i18next.t('pmwjs_image'),
          'aria-hidden': 'true',
        },
        src: window.PMW.util.asset_url('images/emailmaker/image_placeholder.png'),
        style: {
          width: '50%',
          '--mobile-width': '100%',
          'max-width': '100%',
          display: 'block',
          height: 'auto',
          'border-radius': '0',
          'border-color': '#FFFFFF',
          'border-width': '0px',
        },
        [EMAIL_MAKER_PROPS.CONTENT_SOURCE]: '',
        [EMAIL_MAKER_PROPS.CROP_DATA]: undefined,
        [EMAIL_MAKER_PROPS.HASHED_FILENAME]: '',
        [EMAIL_MAKER_PROPS.IS_MEDIA]: true,
        [EMAIL_MAKER_PROPS.IS_CROPPABLE]: true,
        [EMAIL_MAKER_PROPS.MEDIA_TYPE]: EmailMakerMediaType.IMAGE,
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        editor.runCommand(EMAIL_MAKER_COMMANDS.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, {component: this});
        editor.getModel().skip(() => {
          const source = getStringProp(this, EMAIL_MAKER_PROPS.CONTENT_SOURCE);
          const hashedFilename = getStringProp(this, EMAIL_MAKER_PROPS.HASHED_FILENAME) ?? '';

          if (source === MULTI_MEDIA_SOURCE_GETTY) {
            editor.runCommand(EMAIL_MAKER_COMMANDS.SET_IMAGE_SRC, {
              source,
              component: this,
              hashedFilename,
            });
          }
        });
      },
    },
    view: {
      onActive(this: ComponentViewDefinition & ComponentView, ev: Event | undefined) {
        if (ev) {
          ev.stopPropagation();
          ev.preventDefault();
        }

        switch (this.model.get(EMAIL_MAKER_PROPS.MEDIA_MODAL)) {
          case EmailMakerMediaImageModal.MY_UPLOADS:
            editor.runCommand(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_MY_UPLOADS_IMAGE, {
              component: this.model,
            });
            break;
          case EmailMakerMediaImageModal.EXPLORE_MEDIA:
            editor.runCommand(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_EXPLORE_MEDIA_IMAGE, {
              component: this.model,
            });
            break;
          case EmailMakerMediaImageModal.AI_IMAGES:
            editor.runCommand(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_AI_IMAGES, {
              component: this.model,
            });
            break;
          default:
            editor.runCommand(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_ADD_IMAGE_OPTION_SELECTOR, {
              component: this.model,
            });
        }
      },
      events: asViewEvents({
        dblclick: openChangeImageModal,
        touchend: dispatchDblClickOnDoubleTap,
        click: mediaClickEventHandler,
        ...mediaHoverEventHandlers,
        ...RESTORED_GRAPESJS_IMAGE_VIEW_EVENTS,
      }),
    },
    isComponent: matchByTagAndAttr('IMG', EMAIL_MAKER_PROPS.IS_PMW_IMAGE, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE,
    model: {
      defaults: {
        trackingName: 'Video',
        name: window.i18next.t('pmwjs_video'),
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        attributes: {
          'data-is-pmw-video': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO,
          class: 'pmw-media-type',
          alt: window.i18next.t('pmwjs_video'),
          'aria-hidden': 'true',
        },
        style: {
          'border-radius': '0',
          display: 'block',
          height: 'auto',
          width: '50%',
          '--mobile-width': '100%',
          'max-width': '100%',
        },
        src: window.PMW.util.asset_url('images/emailmaker/video_placeholder.png'),
        [EMAIL_MAKER_PROPS.MEDIA_TYPE]: EmailMakerMediaType.VIDEO,
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        editor.runCommand(EMAIL_MAKER_COMMANDS.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, {component: this});
      },
    },
    view: {
      onActive(this: ComponentViewDefinition & ComponentView, ev: Event | undefined) {
        if (ev) {
          ev.stopPropagation();
          ev.preventDefault();
        }

        editor.runCommand(EMAIL_MAKER_COMMANDS.mediaModals.OPEN_ADD_VIDEO_OPTION_SELECTOR, {
          component: this.model,
        });
      },
      events: mediaViewEvents(dispatchDblClickOnDoubleTap, mediaHoverEventHandlers),
    },
    isComponent: matchByTagAndAttr('IMG', 'data-is-pmw-video', EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE,
    model: {
      defaults: {
        trackingName: 'Sticker',
        name: window.i18next.t('pmwjs_sticker'),
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        attributes: {
          'data-is-pmw-sticker': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER,
          class: 'pmw-media-type',
          alt: window.i18next.t('pmwjs_sticker'),
          'aria-hidden': 'true',
        },
        style: {
          'border-radius': '0',
          width: '50%',
          '--mobile-width': '100%',
          'max-width': '100%',
          display: 'block',
          height: 'auto',
        },
        src: window.PMW.util.asset_url('images/emailmaker/sticker_placeholder.png'),
        [EMAIL_MAKER_PROPS.MEDIA_TYPE]: EmailMakerMediaType.STILL_STICKER,
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        editor.runCommand(EMAIL_MAKER_COMMANDS.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, {component: this});
      },
    },
    view: {
      onActive(this: ComponentViewDefinition & ComponentView) {
        editor.runCommand(EMAIL_MAKER_COMMANDS.OPEN_STICKER_MODAL, {component: this.model});
      },
      events: mediaViewEvents(dispatchDblClickOnDoubleTap, mediaHoverEventHandlers),
    },
    isComponent: matchByTagAndAttr('IMG', 'data-is-pmw-sticker', EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_ICON, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE,
    model: {
      defaults: {
        trackingName: 'Icon',
        name: window.i18next.t('pmwjs_icon'),
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        attributes: {
          'data-is-pmw-icon': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_ICON,
          class: 'pmw-media-type',
          alt: window.i18next.t('pmwjs_icon'),
          'aria-hidden': 'true',
        },
        style: {
          'border-radius': '0',
          width: '25%',
          '--mobile-width': '50%',
          'max-width': '100%',
          display: 'block',
          height: 'auto',
        },
        src: window.PMW.util.asset_url('images/emailmaker/icon_placeholder.png'),
        [EMAIL_MAKER_PROPS.MEDIA_TYPE]: EmailMakerMediaType.ICON,
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        editor.runCommand(EMAIL_MAKER_COMMANDS.UPDATE_IMAGE_ALIGNMENT_COMPATIBILITY, {component: this});
      },
    },
    view: {
      onActive(this: ComponentViewDefinition & ComponentView) {
        editor.runCommand(EMAIL_MAKER_COMMANDS.OPEN_ICONS_MODAL, {component: this.model});
      },
      events: mediaViewEvents(dispatchDblClickOnDoubleTap, mediaHoverEventHandlers),
    },
    isComponent: matchByTagAndAttr('IMG', 'data-is-pmw-icon', EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_ICON),
  });
}
